import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { CommentThread, CommentContextType } from '../../shared/types/comments';
import { addColorOpacity } from '../../shared/utils/styles';
import { decodeHtmlEntities } from '../../shared/utils/html-entities';
import { Ionicons } from '@expo/vector-icons';

interface ThreadCardProps {
  thread: CommentThread;
  onPress: (thread: CommentThread) => void;
}

const getContextIcon = (contextType: CommentContextType, color: string, size: number = 16) => {
  const iconMap: Record<CommentContextType, keyof typeof Ionicons.glyphMap> = {
    portfolio: 'folder',
    property: 'home',
    financial_entry: 'cash',
    mortgage_calculation: 'calculator',
    due_diligence_item: 'checkmark-circle',
    survey_item: 'clipboard',
    meeting: 'calendar',
    task: 'checkbox',
    document: 'document-text',
  };
  return <Ionicons name={iconMap[contextType] || 'chatbubbles'} size={size} color={color} />;
};

const getContextLabel = (contextType: CommentContextType): string => {
  const labels: Record<CommentContextType, string> = {
    portfolio: 'Portfolio',
    property: 'Property',
    financial_entry: 'Financial',
    mortgage_calculation: 'Mortgage',
    due_diligence_item: 'Due Diligence',
    survey_item: 'Survey',
    meeting: 'Meeting',
    task: 'Task',
    document: 'Document',
  };
  return labels[contextType] || contextType;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export function ThreadCard({ thread, onPress }: ThreadCardProps) {
  const styles = useStyles();
  const responsive = useResponsive();
  const { isMobile } = responsive;

  const hasUnread = thread.unread_count && thread.unread_count > 0;
  const commentCount = thread.comment_count || 0;

  return (
    <Pressable
      style={({ pressed }) => {
        const baseStyle = {
          backgroundColor: styles.tokens.color.surface,
          borderRadius: styles.utils.radius.md,
          padding: styles.utils.spacing.md,
          marginBottom: styles.utils.spacing.sm,
          borderWidth: 1,
          borderColor: hasUnread ? styles.tokens.color.primary : styles.tokens.color.border,
          opacity: pressed ? 0.7 : 1,
        };
        if (hasUnread) {
          return {
            ...baseStyle,
            borderLeftWidth: 3,
            borderLeftColor: styles.tokens.color.primary,
          };
        }
        return baseStyle;
      }}
      onPress={() => onPress(thread)}
    >
      {/* Header Row: Title and Pinned */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: styles.utils.spacing.xs }}>
        <View style={{ flex: 1, marginRight: styles.utils.spacing.sm }}>
          <Text
            style={{
              fontSize: styles.utils.typography.sizes.base,
              fontWeight: hasUnread
                ? styles.utils.typography.weights.semibold
                : styles.utils.typography.weights.medium,
              color: hasUnread ? styles.tokens.color.onSurface : addColorOpacity(styles.tokens.color.onSurface, 0.9),
              fontFamily: styles.utils.typography.body.fontFamily,
              lineHeight: styles.utils.typography.sizes.base * 1.4,
            }}
            numberOfLines={2}
          >
            {decodeHtmlEntities(thread.thread_title || 'Untitled Discussion')}
          </Text>
        </View>
        {thread.is_pinned && (
          <Ionicons
            name="pin"
            size={14}
            color={styles.tokens.color.primary}
            style={{ marginTop: 2 }}
          />
        )}
      </View>

      {/* Meta Row: Context, Count, Time */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: styles.utils.spacing.sm,
          flexWrap: 'wrap',
        }}
      >
        {/* Context Badge */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: styles.utils.spacing.xs,
            backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.1),
            paddingHorizontal: styles.utils.spacing.sm,
            paddingVertical: 4,
            borderRadius: styles.utils.radius.full,
          }}
        >
          {getContextIcon(thread.context_type, styles.tokens.color.primary, 12)}
          <Text
            style={{
              fontSize: styles.utils.typography.sizes.xs,
              color: styles.tokens.color.primary,
              fontWeight: styles.utils.typography.weights.medium,
              fontFamily: styles.utils.typography.body.fontFamily,
            }}
          >
            {getContextLabel(thread.context_type)}
          </Text>
        </View>

        {/* Comment Count */}
        <Text
          style={{
            fontSize: styles.utils.typography.sizes.xs,
            color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
            fontFamily: styles.utils.typography.body.fontFamily,
          }}
        >
          {commentCount} {commentCount === 1 ? 'reply' : 'replies'}
        </Text>

        {/* Unread Badge */}
        {hasUnread && (
          <View
            style={{
              backgroundColor: styles.tokens.color.primary,
              borderRadius: styles.utils.radius.full,
              paddingHorizontal: styles.utils.spacing.sm,
              paddingVertical: 2,
              minWidth: 20,
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                fontSize: styles.utils.typography.sizes.xs,
                color: styles.tokens.color.onPrimary,
                fontWeight: styles.utils.typography.weights.bold,
                fontFamily: styles.utils.typography.body.fontFamily,
              }}
            >
              {thread.unread_count}
            </Text>
          </View>
        )}

        {/* Time */}
        <Text
          style={{
            fontSize: styles.utils.typography.sizes.xs,
            color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
            fontFamily: styles.utils.typography.body.fontFamily,
            marginLeft: 'auto',
          }}
        >
          {formatDate(thread.updated_at)}
        </Text>
      </View>

      {/* Context Name (if available) */}
      {thread.context_name && (
        <Text
          style={{
            fontSize: styles.utils.typography.sizes.xs,
            color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
            marginTop: styles.utils.spacing.xs,
            fontFamily: styles.utils.typography.body.fontFamily,
          }}
          numberOfLines={1}
        >
          {thread.context_name}
        </Text>
      )}
    </Pressable>
  );
}

